import * as React from 'react'
import { Link as TanstackRouterLink, type LinkOptions, useSearch } from '@tanstack/react-router'
import { type VariantProps } from 'class-variance-authority'
import { LoaderCircleIcon } from 'lucide-react'

import { useExtractRootSearchParams, useNavigate } from '@/routes'
import { confirmNavigation } from '@/routes/navigationConfirmation'
import type { NavigationTarget } from '@/routes/navigationTarget'
import { cn } from '@/components/new/lib/utils'
import { buttonVariants } from '@/components/new/ui/button'

type LinkNavigationProps = NavigationTarget

type LinkVariant = VariantProps<typeof buttonVariants>['variant'] | 'unstyled'

type LinkProps = LinkNavigationProps &
    Omit<React.ComponentPropsWithoutRef<'a'>, 'href' | 'onClick'> &
    Omit<VariantProps<typeof buttonVariants>, 'variant'> & {
        variant?: LinkVariant
        activeClassName?: string
        inactiveClassName?: string
        active?: boolean
        className?: string
        style?: React.CSSProperties
        title?: string
        children?: React.ReactNode
        nonLinkElement?: React.ElementType
        disabled?: boolean
        loading?: boolean
    }

type RouterLinkProps = React.HTMLAttributes<HTMLAnchorElement> & {
    className?: string
    inactiveClassName?: string
    activeClassName?: string
    to: LinkOptions['to']
    params?: LinkOptions['params']
    search?: LinkOptions['search']
    activeOptions?: LinkOptions['activeOptions']
    skipNavigationConfirm?: boolean
    style?: React.CSSProperties
    target?: string
    title?: string
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
    children?: React.ReactNode
}

const RouterLink = React.forwardRef<HTMLAnchorElement, RouterLinkProps>(
    (
        {
            className,
            inactiveClassName,
            activeClassName,
            to,
            params,
            search,
            activeOptions,
            skipNavigationConfirm = false,
            style,
            target,
            title,
            onClick,
            children,
            ...props
        },
        ref
    ) => {
        const extractRootSearchParams = useExtractRootSearchParams()
        const navigate = useNavigate()
        const searchParams = useSearch({ strict: false })

        const rootSearchParams = React.useMemo(() => {
            return extractRootSearchParams(searchParams)
        }, [extractRootSearchParams, searchParams])

        const handleClick = React.useCallback(
            async (event: React.MouseEvent<HTMLAnchorElement>) => {
                onClick(event)

                if (target === '_blank' || event.defaultPrevented || event.button !== 0) {
                    return
                }

                if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
                    return
                }

                event.preventDefault()

                if (skipNavigationConfirm || (await confirmNavigation())) {
                    navigate({ to, params, search })
                }
            },
            [navigate, onClick, params, search, skipNavigationConfirm, target, to]
        )

        return (
            <TanstackRouterLink
                ref={ref}
                data-slot="link"
                {...props}
                className={className}
                inactiveProps={{ className: inactiveClassName }}
                activeProps={{
                    className: activeClassName,
                    'aria-current': 'page',
                }}
                to={to}
                params={params}
                search={{
                    ...rootSearchParams,
                    ...search,
                }}
                activeOptions={activeOptions}
                title={title}
                target={target}
                style={style}
                onClick={(event) => {
                    void handleClick(event)
                }}
            >
                {children}
            </TanstackRouterLink>
        )
    }
)

RouterLink.displayName = 'RouterLink'

const Link = React.forwardRef<HTMLAnchorElement | HTMLSpanElement, LinkProps>(
    (
        {
            className,
            variant = 'link',
            size = 'default',
            loading = false,
            activeClassName,
            inactiveClassName,
            active,
            to,
            params,
            search,
            activeOptions,
            href,
            target,
            onClick,
            title,
            nonLinkElement,
            children,
            disabled,
            skipNavigationConfirm,
            style,
            ...props
        },
        ref
    ) => {
        if (disabled) {
            return children
        }

        const mergedClassName = cn(
            variant !== 'unstyled' && buttonVariants({ variant, size }),
            loading && 'pointer-events-none opacity-50',
            className,
            ((!!to && active === false) || (!to && !active)) && inactiveClassName,
            active === true && activeClassName
        )

        const handleAction: React.MouseEventHandler<HTMLAnchorElement> | undefined = onClick
        const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation()
            handleAction?.(event)
        }

        if (to) {
            return (
                <RouterLink
                    {...props}
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                    className={mergedClassName}
                    inactiveClassName={inactiveClassName}
                    activeClassName={activeClassName}
                    to={to}
                    params={params}
                    search={search}
                    activeOptions={activeOptions}
                    skipNavigationConfirm={skipNavigationConfirm}
                    style={style}
                    target={target}
                    title={title}
                    onClick={handleClick}
                >
                    {children}
                    {loading && <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />}
                </RouterLink>
            )
        }

        if (href) {
            return (
                <a
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                    data-slot="link"
                    {...props}
                    className={mergedClassName}
                    style={style}
                    href={href}
                    target={target}
                    title={title}
                    onClick={handleClick}
                >
                    {children}
                    {loading && <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />}
                </a>
            )
        }

        const Element = nonLinkElement || 'span'

        return (
            <Element
                ref={ref as React.ForwardedRef<HTMLSpanElement>}
                data-slot="link"
                {...props}
                className={mergedClassName}
                style={style}
                title={title}
                onClick={onClick as React.MouseEventHandler<HTMLSpanElement>}
            >
                {children}
                {loading && <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />}
            </Element>
        )
    }
)

Link.displayName = 'Link'

export { Link }
export type { LinkNavigationProps, LinkProps }
